/**
 * Tiny in-house toast — avoids pulling in a full library when we only
 * need 2-3 notifications visible at once.
 */

import { create } from 'zustand';
import { useEffect } from 'react';
import { cn } from '../lib/utils';

interface Toast {
  id: number;
  title: string;
  description?: string;
  variant?: 'default' | 'success' | 'destructive';
}

interface ToastState {
  toasts: Toast[];
  push: (t: Omit<Toast, 'id'>) => void;
  dismiss: (id: number) => void;
}

let nextId = 1;
const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => set((s) => ({ toasts: [...s.toasts, { ...t, id: nextId++ }] })),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export function toast(t: Omit<Toast, 'id'>) {
  useToastStore.getState().push(t);
}

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    const timers = toasts.map((t) => setTimeout(() => dismiss(t.id), 4500));
    return () => timers.forEach(clearTimeout);
  }, [toasts, dismiss]);

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cn(
            'clay bg-background p-4 animate-in slide-in-from-bottom-5',
            t.variant === 'success' && 'border-green-500/50',
            t.variant === 'destructive' && 'border-destructive/50 bg-destructive/5',
          )}
        >
          <div className="font-semibold">{t.title}</div>
          {t.description && <div className="text-sm text-muted-foreground mt-1">{t.description}</div>}
        </div>
      ))}
    </div>
  );
}
